/**
 * All visible SDK surface: the consent dialog, the persistent session indicator, and the
 * highlight ring.
 *
 * Two rules this file must never break:
 *  1. Everything lives inside a shadow root, so host styles cannot leak in and SDK styles
 *     cannot leak out. No style-src exemption is needed. The root is OPEN on purpose: we
 *     ask integrators to inspect exactly what we render, and their QA has to be able to
 *     drive the consent dialog. A closed root would block that while protecting very
 *     little, since the host page could simply decline to load the SDK at all.
 *  2. The highlight NEVER intercepts input. A decorative overlay that can swallow a click
 *     will eventually appear to the customer as a frozen page.
 */
const ROOT_ID = "cn-cobrowse-root";

export interface OverlayOptions {
  highlightColor?: string;
  zIndex?: number;
  audioCue?: boolean;
  announce?: boolean;
}

export class Overlay {
  private host?: HTMLElement;
  private root?: ShadowRoot;
  /** One ring per highlighted control; a step may point at several at once. */
  private rings: HTMLElement[] = [];
  /** The element each ring is following, index-aligned with `rings`. */
  private trackedAll: HTMLElement[] = [];
  private indicatorEl?: HTMLElement;
  private raf = 0;
  private color: string;
  private z: number;
  private cue: boolean;
  private announces: boolean;
  private liveEl?: HTMLElement;
  private audio?: AudioContext;
  /** What we last announced, so a re-assert of the same target does not repeat itself. */
  private lastAnnounced = "";

  constructor(opts: OverlayOptions = {}) {
    this.cue = opts.audioCue === true;
    this.announces = opts.announce !== false;
    // Amber, and it pulses. A static ring in a brand colour reads as page furniture and
    // customers look straight past it; the pulse is what makes someone on a phone call
    // actually find the box you are talking about.
    this.color = opts.highlightColor || "#ffd60a";
    this.z = opts.zIndex ?? 2147483000;
  }

  private ensure(): ShadowRoot {
    if (this.root) return this.root;
    const host = document.createElement("div");
    host.id = ROOT_ID;
    host.setAttribute("data-cobrowse-ignore", "");   // never observe our own UI
    host.style.cssText = "all:initial;position:fixed;top:0;left:0;width:0;height:0;";
    (document.body || document.documentElement).appendChild(host);
    const root = host.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = `
      :host { all: initial; }
      .ring { position: fixed; pointer-events: none; border-radius: 6px;
              border: 2px solid ${this.color};
              transition: top .12s, left .12s, width .12s, height .12s; z-index: ${this.z};
              animation: cn-pulse 1.6s ease-in-out infinite; }
      @keyframes cn-pulse {
        0%, 100% { box-shadow: 0 0 0 4px  ${this.color}4d, 0 0 20px ${this.color}b3; }
        50%      { box-shadow: 0 0 0 11px ${this.color}14, 0 0 36px ${this.color}fa; }
      }
      /* Respect a customer who has asked their OS for less motion: keep the ring,
         drop the animation. */
      @media (prefers-reduced-motion: reduce) {
        .ring { animation: none; box-shadow: 0 0 0 4px ${this.color}4d, 0 0 20px ${this.color}b3; }
      }
      .badge { position: fixed; bottom: 16px; right: 16px; z-index: ${this.z + 1};
               display: flex; align-items: center; gap: 8px; background: #0E1530; color: #fff;
               font: 13px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
               padding: 9px 12px; border-radius: 999px; box-shadow: 0 6px 22px rgba(0,0,0,.28); }
      .dot { width: 8px; height: 8px; border-radius: 50%; background: #46D18B; flex: 0 0 auto; }
      .badge button { all: unset; cursor: pointer; color: #9FC2E8; font-weight: 600; padding-left: 6px; }
      .badge button:hover { color: #fff; text-decoration: underline; }
      /* Bottom-right is exactly where a mobile form puts its primary action, so on a
         narrow screen the badge sat on top of the very button being highlighted — the
         customer could not see the thing they were being told to tap. Declared AFTER
         .badge so it wins; a media query carries no extra specificity of its own. */
      @media (max-width: 480px) {
        .badge { top: 8px; right: 8px; bottom: auto; left: auto;
                 padding: 7px 10px; font-size: 12px; }
      }
      .scrim { position: fixed; inset: 0; background: rgba(10,16,32,.45); z-index: ${this.z + 2};
               display: flex; align-items: center; justify-content: center; padding: 20px; }
      .card { background: #fff; color: #141B46; max-width: 380px; width: 100%; border-radius: 12px;
              padding: 22px; box-shadow: 0 18px 48px rgba(0,0,0,.3);
              font: 14px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
      .card h2 { margin: 0 0 8px; font-size: 16px; color: #1B2A5E; }
      .card p { margin: 0 0 8px; color: #4A5570; }
      .card ul { margin: 0 0 16px; padding-left: 18px; color: #4A5570; }
      .card li { margin-bottom: 4px; }
      .row { display: flex; gap: 10px; justify-content: flex-end; }
      .row button { all: unset; cursor: pointer; padding: 9px 16px; border-radius: 8px; font-weight: 600; }
      /* Announced, never shown: the visual channel is the ring. */
      .sr { position: fixed; width: 1px; height: 1px; overflow: hidden;
            clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; }
      .no { color: #5B6480; } .no:hover { background: #F1F3F8; }
      .yes { background: ${this.color}; color: #fff; } .yes:hover { filter: brightness(1.08); }
      @media (prefers-reduced-motion: reduce) { .ring { transition: none; } }
    `;
    root.appendChild(style);
    this.host = host; this.root = root;
    return root;
  }

  /** Built-in consent dialog. Resolves true when the customer accepts. */
  consent(): Promise<boolean> {
    const root = this.ensure();
    // Dismiss the soft keyboard first. If the customer was typing when assistance
    // started, the keyboard covers the lower half of a phone screen — including this
    // dialog. They never see the thing they are supposed to agree to, and the session
    // just appears not to start.
    try {
      const active = document.activeElement as HTMLElement | null;
      if (active && active !== document.body && typeof active.blur === "function") active.blur();
    } catch { /* nothing focused, or a browser that disagrees: harmless */ }
    return new Promise<boolean>((resolve) => {
      const scrim = document.createElement("div");
      scrim.className = "scrim";
      scrim.innerHTML = `
        <div class="card" role="dialog" aria-modal="true" aria-label="Allow guided assistance">
          <h2>Allow guided assistance?</h2>
          <p>A support assistant will help you complete this on your screen.</p>
          <ul>
            <li>It highlights what to do next.</li>
            <li>It <strong>cannot see what you type</strong>.</li>
            <li>You can stop it at any time.</li>
          </ul>
          <div class="row">
            <button class="no" type="button">Not now</button>
            <button class="yes" type="button">Allow</button>
          </div>
        </div>`;
      const done = (v: boolean) => { try { scrim.remove(); } catch { /* already gone */ } resolve(v); };
      scrim.querySelector<HTMLElement>(".no")!.onclick = () => done(false);
      scrim.querySelector<HTMLElement>(".yes")!.onclick = () => done(true);
      root.appendChild(scrim);
    });
  }

  /** Persistent "assistance is active" badge with a way to end the session. */
  showIndicator(onEnd: () => void): void {
    const root = this.ensure();
    if (this.indicatorEl) return;
    const el = document.createElement("div");
    el.className = "badge";
    el.innerHTML = `<span class="dot"></span><span>Assistance active</span><button type="button">End</button>`;
    el.querySelector("button")!.addEventListener("click", onEnd);
    root.appendChild(el);
    this.indicatorEl = el;
  }

  hideIndicator(): void {
    try { this.indicatorEl?.remove(); } catch { /* already gone */ }
    this.indicatorEl = undefined;
  }

  /**
   * A short, quiet tone. Two hundred milliseconds at 880Hz with an eased tail, because a
   * square-edged beep on a payment page reads as an error sound.
   *
   * Everything here is best-effort: autoplay policy blocks audio until the customer has
   * interacted with the page, older browsers lack the constructor, and neither is worth
   * a broken session — the ring is the primary signal and the tone is the reminder.
   */
  private chime(): void {
    if (!this.cue) return;
    try {
      type Ctor = typeof AudioContext;
      const AC: Ctor | undefined =
        (window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor }).AudioContext ??
        (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;
      if (!AC) return;
      this.audio ??= new AC();
      const ctx = this.audio;
      if (ctx.state === "suspended") void ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      const t = ctx.currentTime;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.16, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.22);
    } catch { /* audio is a courtesy, never a requirement */ }
  }

  /**
   * Tell assistive technology what is being pointed at.
   *
   * The ring is a purely visual affordance: a customer using a screen reader gets
   * nothing from it, and a customer on a phone call may not be looking at the screen at
   * all. `role="status"` is polite, so it waits for a gap rather than interrupting.
   */
  private announce(labels: string[]): void {
    if (!this.announces) return;
    const text = labels.filter(Boolean).join(", ");
    if (!text || text === this.lastAnnounced) return;
    this.lastAnnounced = text;
    try {
      const root = this.ensure();
      if (!this.liveEl) {
        const el = document.createElement("div");
        el.className = "sr";
        el.setAttribute("role", "status");
        el.setAttribute("aria-live", "polite");
        root.appendChild(el);
        this.liveEl = el;
      }
      // Clearing first makes a repeat announcement of changed content reliable across
      // screen readers, which otherwise sometimes ignore an identical-looking update.
      this.liveEl.textContent = "";
      const el = this.liveEl;
      setTimeout(() => { try { el.textContent = `Assistant is highlighting: ${text}`; } catch { /* gone */ } }, 30);
    } catch { /* announcements never break a session */ }
  }

  /** Draw a ring around one element. Convenience wrapper over `highlightAll`. */
  highlight(el: HTMLElement | null): void {
    this.highlightAll(el ? [el] : []);
  }

  /**
   * Ring EVERY element in `els`, and nothing else.
   *
   * A step is not always one control. "Pick a card category" and "choose your bank" are
   * choices among five or six tiles, and ringing only the first silently steers the customer
   * toward it — on a regulated journey that is mis-selling by interface. So the overlay
   * carries a SET of rings, and the caller decides whether that set has one member or six.
   *
   * Scroll-into-view targets the first element only: with several rings on screen the
   * customer needs to see the group, and yanking the page to each in turn is worse than
   * leaving it where the first one lands.
   */
  highlightAll(els: HTMLElement[]): void {
    const root = this.ensure();
    const targets = (els || []).filter(Boolean);
    if (!targets.length) { this.clearHighlight(); return; }

    const firstTime = this.rings.length === 0;
    // Cue only on a CHANGE of target. The service re-asserts the current highlight on
    // every reconnect and after every page publish, and a chime on each of those would
    // turn a slow form into a slot machine.
    const changed = targets.length !== this.trackedAll.length ||
      targets.some((el, i) => el !== this.trackedAll[i]);
    // Reuse the rings we already have; add or drop only the difference, so a step that
    // re-publishes the same targets does not flicker.
    while (this.rings.length > targets.length) {
      try { this.rings.pop()?.remove(); } catch { /* already gone */ }
    }
    while (this.rings.length < targets.length) {
      const ring = document.createElement("div");
      ring.className = "ring";
      root.appendChild(ring);
      this.rings.push(ring);
    }
    this.trackedAll = targets;

    if (firstTime) {
      // Reposition on the events that actually move things. requestAnimationFrame is a
      // smoothness enhancement ONLY: it does not run in a hidden or background tab, so a
      // ring positioned solely from a rAF loop stays parked at 0,0 until the customer
      // returns to the tab. Everything below works whether or not frames are being served.
      addEventListener("scroll", this.place, { passive: true, capture: true });
      addEventListener("resize", this.place, { passive: true });
      document.addEventListener("visibilitychange", this.place);
      this.startFollow();
    }
    this.place();                       // place them NOW, synchronously
    try { targets[0].scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* older browsers */ }
    setTimeout(this.place, 350);        // and again once smooth scrolling has settled

    if (changed) {
      this.chime();
      this.announce(targets.map((el) => (el.getAttribute("aria-label") || el.textContent || "").trim().slice(0, 80)));
    }
  }

  /** Put every ring where its target currently is. Cheap, idempotent, safe to call often. */
  private place = (): void => {
    for (let i = 0; i < this.rings.length; i++) {
      const el = this.trackedAll[i];
      const ring = this.rings[i];
      if (!el || !ring) continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;  // target went away; keep last position
      const s = ring.style;
      s.top = `${r.top - 3}px`; s.left = `${r.left - 3}px`;
      s.width = `${r.width + 6}px`; s.height = `${r.height + 6}px`;
    }
  };

  private startFollow(): void {
    const tick = () => {
      if (!this.rings.length) return;
      this.place();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  clearHighlight(): void {
    this.lastAnnounced = "";
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
    removeEventListener("scroll", this.place, { capture: true } as EventListenerOptions);
    removeEventListener("resize", this.place);
    document.removeEventListener("visibilitychange", this.place);
    for (const ring of this.rings) { try { ring.remove(); } catch { /* already gone */ } }
    this.rings = [];
    this.trackedAll = [];
  }

  destroy(): void {
    this.clearHighlight();
    this.hideIndicator();
    try { void this.audio?.close(); } catch { /* already closed */ }
    this.audio = undefined;
    this.liveEl = undefined;
    try { this.host?.remove(); } catch { /* already gone */ }
    this.host = undefined; this.root = undefined;
  }
}
